import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";

interface BlogMarkdownRendererProps {
  content: string;
}

export const BlogMarkdownRenderer = ({ content }: BlogMarkdownRendererProps) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h2: ({ children }) => (
          <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-4 text-foreground border-b border-border pb-2">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3 text-foreground">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-lg font-semibold mt-6 mb-2 text-foreground">
            {children}
          </h4>
        ),
        p: ({ children }) => (
          <p className="text-base sm:text-lg leading-relaxed text-muted-foreground mb-6">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-6 mb-6 space-y-2 text-muted-foreground">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-6 mb-6 space-y-2 text-muted-foreground">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="text-base sm:text-lg leading-relaxed">{children}</li>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-primary bg-primary/5 pl-6 pr-4 py-4 my-8 rounded-r-lg italic text-muted-foreground">
            {children}
          </blockquote>
        ),
        a: ({ href, children }) => {
          if (href?.startsWith("/")) {
            return (
              <Link to={href} className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                {children}
              </Link>
            );
          }
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
              {children}
            </a>
          );
        },
        strong: ({ children }) => (
          <strong className="font-semibold text-foreground">{children}</strong>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-8 rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-muted">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="text-left p-3 font-semibold text-foreground border-b border-border">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="p-3 border-b border-border text-muted-foreground">
            {children}
          </td>
        ),
        hr: () => <hr className="my-10 border-border" />,
        code: ({ children, className }) => {
          const isBlock = className?.includes("language-");
          if (isBlock) {
            return (
              <code className="block bg-muted p-4 rounded-lg text-sm overflow-x-auto my-6 font-mono">
                {children}
              </code>
            );
          }
          return (
            <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">
              {children}
            </code>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
